import type { ContextFinding, ContextRunDetail, ContextRunDownload, FindingSeverity, PipelineSummary } from '@lastrouter/contracts';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, CheckCircle2, ChevronRight, cn, Download, Input, ShieldCheck, Upload, X, XCircle } from '@lastrouter/ui';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../api/client.js';
import { errorMessage, useApiMutation } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { EmptyState, StatusBadge } from '../../components/common.js';
import { saveBase64, toBase64 } from '../../lib/files.js';
import { formatNumber } from '../../lib/format.js';

/*
 * Telas do pipeline de contexto (ADR 0044), passos 5 a 8: resultado, arquivos, validação do cliente, bateria e
 * certificação. Tudo sai do resumo `pipeline` do detalhe da execução; os arquivos vêm da API em base64.
 */

export const SEVERITY_TONE = { critico: 'danger', alto: 'warning', medio: 'neutral', baixo: 'neutral' } as const;
export const SEVERITIES: FindingSeverity[] = ['critico', 'alto', 'medio', 'baixo'];
const MAX_XLSX = 10 * 1024 * 1024;

/**
 * Indicador com rótulo, valor grande e detalhe. Aceita vários números lado a lado (gravidade, catálogo).
 * Fica dentro de um Card: separa por um fio em cima, sem outra caixa (nada de cartão dentro de cartão).
 */
function Tile({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 border-t border-border pt-3">
      <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">{label}</span>
      {children}
      {hint ? <span className="text-[length:var(--lr-text-xs)] text-fg-muted">{hint}</span> : null}
    </div>
  );
}

function Big({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-[length:var(--lr-text-2xl)] font-semibold leading-none text-fg', className)}>{children}</span>;
}

/** Números lado a lado, cada um com o nome embaixo. */
function Split({ items }: { items: { label: string; value: number; className?: string }[] }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((i) => (
        <div key={i.label} className="flex min-w-0 flex-col gap-1">
          <Big className={i.className}>{formatNumber(i.value)}</Big>
          <span className="truncate text-[length:var(--lr-text-xs)] text-fg-secondary">{i.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Passo 5: o resultado em números grandes. */
export function PipelineResult({ p }: { p: PipelineSummary }) {
  const { t } = useTranslation();
  const k = 'assets.generate.pipeline';
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`${k}.resultTitle`)}</CardTitle>
        <CardDescription>{t(`${k}.resultHelp`)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
          <Tile label={t(`${k}.risks`)}>
            <Split
              items={[
                { label: t('assets.generate.severity.critico'), value: p.risks.critical, className: p.risks.critical ? 'text-danger' : '' },
                { label: t('assets.generate.severity.alto'), value: p.risks.high, className: p.risks.high ? 'text-warning' : '' },
                { label: t('assets.generate.severity.medio'), value: p.risks.medium },
                { label: t('assets.generate.severity.baixo'), value: p.risks.low },
              ]}
            />
          </Tile>
          <Tile label={t(`${k}.questions`)} hint={t(`${k}.questionsHint`)}>
            <Big>{t(`${k}.ofTotal`, { n: formatNumber(p.answered_questions), total: formatNumber(p.questions) })}</Big>
          </Tile>
          <Tile label={t(`${k}.catalog`)} hint={t(`${k}.catalogHint`)}>
            <Split
              items={[
                { label: t(`${k}.expose`), value: p.catalog.expose },
                { label: t(`${k}.review`), value: p.catalog.review },
                { label: t(`${k}.hide`), value: p.catalog.hide },
              ]}
            />
          </Tile>
          <Tile label={t(`${k}.quality`)} hint={t(`${k}.qualityHint`)}>
            {p.quality ? (
              <Big>
                {formatNumber(p.quality.today)}% <span className="text-fg-muted">→</span> {formatNumber(p.quality.with_context)}%
              </Big>
            ) : (
              <span className="text-fg-muted">{t(`${k}.noData`)}</span>
            )}
          </Tile>
          <Tile label={t(`${k}.numbers`)} hint={t(`${k}.numbersHint`)}>
            <span className="text-[length:var(--lr-text-md)] font-semibold leading-snug text-fg first-letter:uppercase">{p.numbers ?? t(`${k}.noData`)}</span>
          </Tile>
          <Tile label={t(`${k}.ai`)}>
            {p.interpretation ? (
              <>
                <span className="break-words font-mono text-[length:var(--lr-text-md)] font-semibold text-fg">{p.interpretation.model}</span>
                <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.aiDetail`, p.interpretation)}</span>
              </>
            ) : (
              <span className="text-fg-secondary">{t(`${k}.noAi`)}</span>
            )}
          </Tile>
        </div>
        {p.out_of_proposal.length ? (
          <details className="border-t border-border pt-3">
            <summary className="cursor-pointer font-medium">{t(`${k}.outOfProposal`, { n: p.out_of_proposal.length })}</summary>
            <p className="mt-1 text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.outOfProposalHelp`)}</p>
            <ul className="mt-2 flex flex-col gap-1 text-[length:var(--lr-text-sm)]">
              {p.out_of_proposal.map((x) => (
                <li key={x.item}>
                  <span className="font-medium">{x.item}</span>: <span className="text-fg-secondary">{x.reason}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Riscos agrupados por gravidade; críticos e altos abertos. */
export function FindingGroups({ findings, render }: { findings: ContextFinding[]; render: (f: ContextFinding) => ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col border-b border-border">
      {SEVERITIES.map((sev) => {
        const list = findings.filter((f) => f.severity === sev);
        if (!list.length) return null;
        return (
          <details key={sev} open={sev === 'critico' || sev === 'alto'} className="group border-t border-border">
            <summary className="flex cursor-pointer list-none items-center gap-2 py-3 font-medium [&::-webkit-details-marker]:hidden">
              <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-fg-muted transition-transform group-open:rotate-90 motion-reduce:transition-none" />
              <StatusBadge tone={SEVERITY_TONE[sev]}>{t(`assets.generate.severity.${sev}`)}</StatusBadge>
              <span>{t('assets.generate.pipeline.riskCount', { count: list.length })}</span>
            </summary>
            <ul className="border-t border-border">{list.map(render)}</ul>
          </details>
        );
      })}
    </div>
  );
}

const DOWNLOADS: ContextRunDownload[] = ['checkpoint.xlsx', 'contexto.ossie.yaml', 'vocabulario_qlik_answers.xlsx', 'contexto.json'];

/** Baixa um arquivo da execução e salva com o nome certo. */
function useDownload(runId: string) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<ContextRunDownload | null>(null);
  async function download(name: ContextRunDownload) {
    setBusy(name);
    try {
      const f = await api('contextRunFile', { params: { id: runId, name } });
      saveBase64(f.name, f.content_type, f.base64);
    } catch (err) {
      toast.error(errorMessage(t, err));
    } finally {
      setBusy(null);
    }
  }
  return { busy, download };
}

function DownloadButton({ runId, name, variant }: { runId: string; name: ContextRunDownload; variant?: 'primary' | 'secondary' }) {
  const { t } = useTranslation();
  const { busy, download } = useDownload(runId);
  return (
    <Button variant={variant ?? 'secondary'} disabled={busy !== null} onClick={() => void download(name)}>
      <Download aria-hidden="true" />
      {t(`assets.generate.pipeline.files.${name.replace(/\./g, '_')}`)}
    </Button>
  );
}

export function PipelineDownloads({ runId, p }: { runId: string; p: PipelineSummary }) {
  const { t } = useTranslation();
  const names = DOWNLOADS.filter((n) => p.files.includes(n));
  if (!names.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('assets.generate.pipeline.downloadTitle')}</CardTitle>
        <CardDescription>{t('assets.generate.pipeline.downloadHelp')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {names.map((n) => (
          <DownloadButton key={n} runId={runId} name={n} variant="secondary" />
        ))}
      </CardContent>
    </Card>
  );
}


/** Para a geração ou a bateria que está rodando. Só o admin, que é quem dispara as duas. */
export function CancelRunButton({ runId }: { runId: string }) {
  const { t } = useTranslation();
  const isAdmin = Boolean(useSession().data?.is_admin);
  const cancel = useApiMutation('cancelContextRun', ['contextRun', 'contextRuns']);
  if (!isAdmin) return null;
  return (
    <Button
      variant="secondary"
      disabled={cancel.isPending}
      onClick={() =>
        cancel.mutate(
          { params: { id: runId } },
          { onSuccess: () => toast.success(t('assets.generate.cancelled')), onError: (err) => toast.error(errorMessage(t, err)) },
        )
      }
    >
      <X aria-hidden="true" />
      {t('assets.generate.cancel')}
    </Button>
  );
}

/** Passo 8: o selo de certificação, as notas e cada condição, numa sub-aba própria da Bateria. */
export function CertificationCard({ run, p }: { run: ContextRunDetail; p: PipelineSummary }) {
  const { t } = useTranslation();
  const k = 'assets.generate.pipeline';
  const cert = p.certification;
  const released = cert?.status === 'liberado para produção';
  if (!cert) return <EmptyState>{t(`${k}.noScore`)}</EmptyState>;
  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-semibold',
                  released ? 'bg-success-surface text-success' : 'bg-danger-surface text-danger',
                )}
              >
                {released ? <CheckCircle2 aria-hidden="true" className="size-5" /> : <XCircle aria-hidden="true" className="size-5" />}
                {released ? t(`${k}.released`) : t(`${k}.blocked`)}
              </span>
              {cert.context_version ? <span className="text-[length:var(--lr-text-sm)] text-fg-muted">{t(`${k}.contextVersion`, { v: cert.context_version })}</span> : null}
            </div>
            <div className="grid grid-cols-2 gap-x-6 sm:max-w-md">
              <Tile label={t(`${k}.score`)}>
                <Big>{cert.score === null ? '-' : `${formatNumber(cert.score)}%`}</Big>
              </Tile>
              {cert.critical_score === null ? null : (
                <Tile label={t(`${k}.criticalScore`)}>
                  <Big>{`${formatNumber(cert.critical_score)}%`}</Big>
                </Tile>
              )}
            </div>
            {cert.score === null ? <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.noScore`)}</p> : null}
            <ul className="flex flex-col divide-y divide-border border-y border-border">
              {cert.conditions.map((c) => (
                <li key={c.condition} className="flex gap-3 py-2">
                  {c.ok ? <CheckCircle2 aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-success" /> : <XCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-danger" />}
                  <div className="flex min-w-0 flex-col">
                    <span className="font-medium first-letter:uppercase">
                      <span className="sr-only">{c.ok ? t(`${k}.conditionOk`) : t(`${k}.conditionNo`)}: </span>
                      {c.condition}
                    </span>
                    <span className="break-words text-[length:var(--lr-text-sm)] text-fg-secondary">{c.detail}</span>
                  </div>
                </li>
              ))}
            </ul>
            {p.files.includes('avaliacao.json') ? (
              <div>
                <DownloadButton runId={run.run.id} name="avaliacao.json" />
              </div>
            ) : null}
          </div>
      </CardContent>
    </Card>
  );
}
